import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { orbitApiFetch } from '../../orbit-api/orbitApiClient';
import { useAuth } from '../../auth/AuthProvider';
import { useBrand } from '../../brand/BrandProvider';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';

/**
 * Interne Ops-Knowledge-Base (/v1/kb, orbit-backend). Getrennt von der
 * kunden-seitigen Fehler-KB (pulse /v1/errors). Operator-Bearer + Brand-Origin
 * pro Call (wie Tickets).
 */
function headers(token, brandOrigin) {
  const h = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (brandOrigin) h['Brand-Origin'] = brandOrigin;
  return h;
}

const FIXTURE_LIST = [
  { id: '1', slug: 'harbor-runbook', title: 'Harbor — Private Registry (Runbook)', category: 'infra', tags: ['harbor', 's3'], source: 'repo', sourcePath: 'infra/k8s/apps/harbor.README.md', published: true, updatedAt: new Date().toISOString() },
  { id: '2', slug: 'velero-backup', title: 'Velero — Backup/Restore', category: 'infra', tags: ['backup'], source: 'repo', sourcePath: 'infra/k8s/apps/velero.README.md', published: true, updatedAt: new Date().toISOString() },
];

export function useKbList({ q = '', category = '', source = '' } = {}) {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  const fx = useFixturesMode();
  return useQuery({
    queryKey: ['ops-kb', q, category, source, brandOrigin, fx ? 'fixture' : 'live'],
    enabled: !!brandOrigin,
    queryFn: async () => {
      if (fx) { await fixtureDelay(); return { items: FIXTURE_LIST.filter((a) => !q || a.title.toLowerCase().includes(q.toLowerCase())) }; }
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (category) qs.set('category', category);
      if (source) qs.set('source', source);
      return orbitApiFetch(`/v1/kb?${qs.toString()}`, { headers: headers(auth.getAccessToken(), brandOrigin) });
    },
  });
}

export function useKbArticle(slug) {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  const fx = useFixturesMode();
  return useQuery({
    queryKey: ['ops-kb-article', String(slug ?? ''), brandOrigin, fx ? 'fixture' : 'live'],
    enabled: !!slug && !!brandOrigin,
    queryFn: async () => {
      if (fx) { await fixtureDelay(); return { article: { ...FIXTURE_LIST.find((a) => a.slug === slug), bodyMd: '# Beispiel\n\nRunbook-Inhalt (Fixtures-Modus).' } }; }
      return orbitApiFetch(`/v1/kb/${encodeURIComponent(slug)}`, { headers: headers(auth.getAccessToken(), brandOrigin) });
    },
  });
}

export function useCreateKbArticle() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, payload) => orbitApiFetch('/v1/kb', { method: 'POST', body: payload, headers: headers(auth.getAccessToken(), brandOrigin) }),
    fixture: (payload) => ({ article: { id: 'new', slug: 'neuer-artikel', ...payload } }),
    invalidate: ['ops-kb'],
    successMessage: () => 'Artikel angelegt.',
  });
}

export function useUpdateKbArticle() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id, ...patch }) => orbitApiFetch(`/v1/kb/${encodeURIComponent(id)}`, { method: 'PUT', body: patch, headers: headers(auth.getAccessToken(), brandOrigin) }),
    fixture: () => ({ ok: true }),
    invalidate: ['ops-kb', 'ops-kb-article'],
    successMessage: () => 'Artikel gespeichert.',
  });
}

export function useDeleteKbArticle() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id }) => orbitApiFetch(`/v1/kb/${encodeURIComponent(id)}`, { method: 'DELETE', headers: headers(auth.getAccessToken(), brandOrigin) }),
    fixture: () => ({ ok: true }),
    invalidate: ['ops-kb'],
    successMessage: () => 'Artikel gelöscht.',
  });
}
